"use client";
import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import { Locale } from "@/dto/locale";
import { getCurrencyCodeFromCountry } from "@/utils/currencyUtils";
interface CurrencyContextValue {
  locale: Locale;
  currency: string;
  format: (amount: number) => string;
}
const CurrencyContext = createContext<CurrencyContextValue | undefined>(
  undefined
);
export function CurrencyProvider({
  locale,
  children,
}: Readonly<{
  locale: Locale;
  children: ReactNode;
}>) {
  const [, country] = locale.split("-");
  const currency = useMemo(
    () => getCurrencyCodeFromCountry(country as any),
    [country]
  );

  const format = useCallback(
    (amount: number) => {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
      }).format(amount / 100);
    },
    [locale, currency]
  );

  const value = useMemo(
    () => ({ locale, currency, format }),
    [locale, currency, format]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be inside CurrencyProvider");
  return ctx;
}
